// alterDatabase — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-database' })` op (DSE2 §3.G1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice G1 — CREATE / ALTER / DROP DATABASE UI.
//
// Per-engine surface (per DSE2 dialect.database):
//   - mssql:    ALTER DATABASE name {MODIFY NAME = new |
//                 SET RECOVERY {FULL|SIMPLE|BULK_LOGGED} |
//                 SET COMPATIBILITY_LEVEL = n}
//   - postgres: ALTER DATABASE name {RENAME TO new | OWNER TO r |
//                 SET param = value | RESET param}
//   - mysql:    ALTER {DATABASE|SCHEMA} name {CHARACTER SET = … | COLLATE = …}
//   - oracle:   ALTER PLUGGABLE DATABASE name {OPEN | CLOSE IMMEDIATE}
//   - sqlite:   unsupported.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DatabaseChange, DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterDatabaseArgs {
  readonly databaseName: string;
  readonly quoted?: boolean;
  readonly mysqlSchema?: boolean;
  readonly change: DatabaseChange;
}

export function buildAlterDatabaseEdit(args: AlterDatabaseArgs): DdlEdit {
  return {
    op: 'alter-database',
    databaseName: args.databaseName,
    ...(args.quoted ? { quoted: true } : {}),
    ...(args.mysqlSchema ? { mysqlSchema: true } : {}),
    change: args.change,
  };
}

export async function commitAlterDatabase(
  ctx: VqeDdlEditContext,
  args: AlterDatabaseArgs,
): Promise<void> {
  const edit = buildAlterDatabaseEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_DB_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'mysql', 'oracle',
] as const);

export function isAlterDatabaseSupported(engine: SqlEngine): boolean {
  return ALTER_DB_SUPPORTED.has(engine);
}

export function getAlterDatabaseUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite databases are filesystem files. ATTACH DATABASE manages '
      + 'attachment, but databases themselves are not DDL-managed.'
    );
  }
  return '';
}

export function isNonEmptyDatabaseChange(change: DatabaseChange): boolean {
  return change.mssqlNewName !== undefined
    || change.mssqlRecovery !== undefined
    || change.mssqlCompatibilityLevel !== undefined
    || change.pgsqlRenameTo !== undefined
    || change.pgsqlOwnerTo !== undefined
    || (change.pgsqlSetOptions !== undefined && change.pgsqlSetOptions.length > 0)
    || (change.pgsqlResetOptions !== undefined && change.pgsqlResetOptions.length > 0)
    || change.mysqlCharacterSet !== undefined
    || change.mysqlCollate !== undefined
    || change.oraclePdbOpen !== undefined;
}

export function validateAlterDatabaseForEngine(
  engine: SqlEngine,
  args: AlterDatabaseArgs,
): string | null {
  if (!isAlterDatabaseSupported(engine)) {
    return getAlterDatabaseUnsupportedTooltip(engine);
  }
  if (args.databaseName.trim().length === 0) {
    return 'Database name is required.';
  }
  const c = args.change;
  if (!isNonEmptyDatabaseChange(c)) {
    return 'At least one ALTER DATABASE change is required.';
  }
  if (engine === 'mssql') {
    if (c.pgsqlRenameTo !== undefined || c.pgsqlOwnerTo !== undefined
      || c.pgsqlSetOptions !== undefined || c.pgsqlResetOptions !== undefined) {
      return 'mssql ALTER DATABASE does not accept pgsql-only fields.';
    }
    if (c.mysqlCharacterSet !== undefined || c.mysqlCollate !== undefined) {
      return 'mssql ALTER DATABASE does not accept mysql-only fields.';
    }
    if (c.oraclePdbOpen !== undefined) {
      return 'mssql ALTER DATABASE does not accept oracle-only fields.';
    }
    if (c.mssqlNewName !== undefined && c.mssqlNewName.trim().length === 0) {
      return 'New database name is required.';
    }
  }
  if (engine === 'postgres') {
    if (c.mssqlNewName !== undefined || c.mssqlRecovery !== undefined
      || c.mssqlCompatibilityLevel !== undefined) {
      return 'postgres ALTER DATABASE does not accept mssql-only fields.';
    }
    if (c.mysqlCharacterSet !== undefined || c.mysqlCollate !== undefined) {
      return 'postgres ALTER DATABASE does not accept mysql-only fields.';
    }
    if (c.oraclePdbOpen !== undefined) {
      return 'postgres ALTER DATABASE does not accept oracle-only fields.';
    }
    if (c.pgsqlRenameTo !== undefined && c.pgsqlRenameTo.trim().length === 0) {
      return 'New database name is required.';
    }
  }
  if (engine === 'mysql') {
    if (c.mssqlNewName !== undefined || c.mssqlRecovery !== undefined
      || c.mssqlCompatibilityLevel !== undefined) {
      return 'mysql ALTER DATABASE does not accept mssql-only fields.';
    }
    if (c.pgsqlRenameTo !== undefined || c.pgsqlOwnerTo !== undefined
      || c.pgsqlSetOptions !== undefined || c.pgsqlResetOptions !== undefined) {
      return 'mysql ALTER DATABASE does not accept pgsql-only fields.';
    }
    if (c.oraclePdbOpen !== undefined) {
      return 'mysql ALTER DATABASE does not accept oracle-only fields.';
    }
  }
  if (engine === 'oracle') {
    if (c.mssqlNewName !== undefined || c.mssqlRecovery !== undefined
      || c.mssqlCompatibilityLevel !== undefined) {
      return 'oracle ALTER DATABASE does not accept mssql-only fields.';
    }
    if (c.pgsqlRenameTo !== undefined || c.pgsqlOwnerTo !== undefined
      || c.pgsqlSetOptions !== undefined || c.pgsqlResetOptions !== undefined) {
      return 'oracle ALTER DATABASE does not accept pgsql-only fields.';
    }
    if (c.mysqlCharacterSet !== undefined || c.mysqlCollate !== undefined) {
      return 'oracle ALTER DATABASE does not accept mysql-only fields.';
    }
  }
  return null;
}
